import { useEffect, useMemo, useState } from 'react'
import { Loader2, RefreshCw } from 'lucide-react'
import api from '../../utils/api'

export default function DownloadStatsTab() {
  const [days, setDays] = useState(30)
  const [loading, setLoading] = useState(true)
  const [baiduFunnel, setBaiduFunnel] = useState({
    summary: null,
    daily: [],
    bdVidRows: []
  })

  const fetchData = async (targetDays = days) => {
    setLoading(true)
    try {
      const baiduRes = await api.get('/admin/adbaidu-funnel-stats', { params: { days: targetDays } })
      if (baiduRes.data.success) {
        const payload =
          baiduRes.data && baiduRes.data.data && typeof baiduRes.data.data === 'object' && !Array.isArray(baiduRes.data.data)
            ? baiduRes.data.data
            : baiduRes.data
        setBaiduFunnel({
          summary: payload?.summary || null,
          daily: payload?.daily || [],
          bdVidRows: payload?.bdVidRows || []
        })
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData(days)
  }, [])

  const funnelCards = useMemo(() => {
    const summaryData = baiduFunnel.summary || {}
    return [
      { id: 'visits', label: '有效访问量', value: Number(summaryData.visits || 0) },
      { id: 'bdVids', label: '去重 bd_vid', value: Number(summaryData.bdVids || 0) },
      { id: 'downloads', label: '下载量', value: Number(summaryData.downloads || 0) },
      { id: 'logins', label: '首次登录量', value: Number(summaryData.logins || 0) },
      { id: 'postbackSuccess', label: '回传成功量', value: Number(summaryData.postbackSuccess || 0) },
      { id: 'postbackFailed', label: '回传失败量', value: Number(summaryData.postbackFailed || 0) }
    ]
  }, [baiduFunnel.summary])

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-2">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">百度推广漏斗</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            这里只统计带 `bd_vid` 的 `/adbaidu` 访问，以及后续下载、首次登录和百度回传结果。
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
          >
            <option value={7}>近 7 天</option>
            <option value={30}>近 30 天</option>
            <option value={90}>近 90 天</option>
          </select>
          <button
            type="button"
            onClick={() => fetchData(days)}
            className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            <RefreshCw className="h-4 w-4" />
            刷新
          </button>
        </div>
      </div>

      <div className="space-y-4 rounded-xl border border-blue-100 bg-blue-50/40 p-5 dark:border-blue-900/40 dark:bg-blue-950/20">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {funnelCards.map((item) => (
            <div
              key={item.id}
              className="rounded-xl border border-white/80 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800"
            >
              <div className="text-sm text-gray-500 dark:text-gray-400">{item.label}</div>
              <div className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">{item.value}</div>
            </div>
          ))}
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">日期</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">访问量</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">下载量</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">首次登录量</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">回传成功</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">回传失败</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-10 text-center text-gray-500">
                      <Loader2 className="mx-auto h-6 w-6 animate-spin" />
                    </td>
                  </tr>
                ) : baiduFunnel.daily.length > 0 ? (
                  baiduFunnel.daily.map((row) => (
                    <tr key={row.day} className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-6 py-4 font-mono text-sm text-gray-700 dark:text-gray-200">{row.day}</td>
                      <td className="px-6 py-4 text-right text-sm font-semibold text-gray-700 dark:text-gray-200">{row.visits || 0}</td>
                      <td className="px-6 py-4 text-right text-sm font-semibold text-gray-700 dark:text-gray-200">{row.downloads || 0}</td>
                      <td className="px-6 py-4 text-right text-sm font-semibold text-gray-700 dark:text-gray-200">{row.logins || 0}</td>
                      <td className="px-6 py-4 text-right text-sm font-semibold text-emerald-600 dark:text-emerald-400">{row.postbackSuccess || 0}</td>
                      <td className="px-6 py-4 text-right text-sm font-semibold text-rose-600 dark:text-rose-400">{row.postbackFailed || 0}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-500">
                      暂无数据
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">bd_vid</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">访问量</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">下载量</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">首次登录量</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">回传成功</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-10 text-center text-gray-500">
                      <Loader2 className="mx-auto h-6 w-6 animate-spin" />
                    </td>
                  </tr>
                ) : baiduFunnel.bdVidRows.length > 0 ? (
                  baiduFunnel.bdVidRows.map((row) => (
                    <tr key={row.bdVid} className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-6 py-4 font-mono text-sm text-gray-700 dark:text-gray-200">{row.bdVid}</td>
                      <td className="px-6 py-4 text-right text-sm font-semibold text-gray-700 dark:text-gray-200">{row.visits || 0}</td>
                      <td className="px-6 py-4 text-right text-sm font-semibold text-gray-700 dark:text-gray-200">{row.downloads || 0}</td>
                      <td className="px-6 py-4 text-right text-sm font-semibold text-gray-700 dark:text-gray-200">{row.logins || 0}</td>
                      <td className="px-6 py-4 text-right text-sm font-semibold text-emerald-600 dark:text-emerald-400">{row.postbackSuccess || 0}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-6 py-10 text-center text-sm text-gray-500">
                      暂无数据
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
